import { init as sentryInitFn } from '@sentry/browser';
import { getDataFromPocket } from '../../utils/pocket';
import { PublicContext } from './utils-pocket-models';

// Set this to true if you need to test some changes on the local instance
const SENTRY_FORCE_ENABLE = false;

const InitValidator = {
    itIsProductionDomain() {
        const host = window.location.hostname;

        const isProdAcronisCom = host.endsWith('.acronis.com') && !host.includes('.gcp.') && !host.includes('.webdev.');
        const isProdAcronisEvents = host === 'acronis.events';

        return isProdAcronisCom || isProdAcronisEvents;
    },
    isEnabled() {
        return SENTRY_FORCE_ENABLE || this.itIsProductionDomain();
    },
};

const SOURCE_BLACKLIST = [
    /extensions\//i,
    /chrome-extension:/i,
    /chrome:\/\//i,
    /webkit-masked-url:\/\//i,
    /^https:\/\/cdn\.cookielaw\.org\//i,
    /^https:\/\/munchkin.marketo.net\//i,
    /^https:\/\/www.googletagmanager.com\//i,
    /^https:\/\/script.hotjar.com\//i,
    /ping[.]min[.]js/i,
    /gtm[.]js/i,
    /6si[.]min[.]js/i,
    /clarity[.]js/i,
    /fbevents[.]js/i,
];

const ERRORS_BLACKLIST = [
    // Sentry recommended rules
    'top.GLOBALS',
    'originalCreateNotification',
    'canvas.contentDocument',
    'fb_xd_fragment',
    'conduitPage',
    // Well-known 3rd party integrations
    'MktoForms2',
    'google_tag_manager',
    'unsafe-eval',
    'Munchkin',
    // Custom rules
    /ResizeObserver/i, // probably an error from a vendor lib. Should not affect users
    'The quota has been exceeded', // inaccessible localstorage, usually fired because of OneTrust wrapping
    /Loading chunk \d+ failed/i, // flacky kubectl rollout
    /Failed to fetch/i, // fetch API errors (excluded because without a context it's just a noise)
    /^TypeError: Load failed$/i, // looks like the same as above, but for iOS Safari
    /NetworkError/i, // we can't do anything with users connection quality
    /video-only background media was paused/i, // has no value and besides a completely normal behavior
    /The play\(\) request/i, // same
    /reCAPTCHA placeholder element must be an element or id/i, // judging by breadcrumbs it happens when a user opens a form without OneTrust consent given
    /Object Not Found Matching Id/i, // same
    /Avoided redundant navigation/i, // should not be an error at all
    /evaluating 'r\["@[^"]+"\].toLowerCase'/i, // For fucks sake Safari tries to parse and process ld+json schema, but of course does it wrong
    /NotAllowedError/i, // restrictive environments, not our fault
    /SecurityError/i, // same
    /Non-Error promise rejection captured with value: Timeout/i, // no idea what's this but anyway we don't need to ingest timeout errors
    /Non-Error promise rejection captured with value: invalid_request/i, // OAuth2 error string thrown by a 3rd-party script, not our code and not actionable
    /Object captured as promise rejection with keys: isCanceled/i, // makeCancelable promise wrapper from a 3rd-party lib, normal request cancellation, not actionable
    /CustomEvent/i, // no idea, non-reproducible, mostly Safari
    /\s+fbq/i, // too many and totally useless
    /property 'createElement' is a read-only and non-configurable/i, // microfrontend issues on Solutions. Can be removed after the rework
];

// Sentry's v10 default scrubbing list for header and query param names
const SENSITIVE_DATA_DENYLIST = ['forwarded', '-ip', 'remote-', 'via', '-user'];

export const initSentry = () => {
    if (!InitValidator.isEnabled()) {
        return;
    }

    const publicContext = getDataFromPocket<PublicContext>('public_context')!;

    sentryInitFn({
        debug: SENTRY_FORCE_ENABLE,
        release: `head-site-main@browser@${publicContext.appVersion}`,
        dsn: 'https://cd987e00d8a4970ecf4d96f763fa9798@o394472.ingest.sentry.io/6232185',
        maxBreadcrumbs: 10,
        attachStacktrace: true,
        denyUrls: SOURCE_BLACKLIST,
        ignoreErrors: ERRORS_BLACKLIST,
        sampleRate: 0.1,
        // Sentry v11 collects these by default when `dataCollection` is unset; keep the v10 restrictive baseline
        dataCollection: {
            userInfo: false,
            cookies: false,
            httpHeaders: {
                request: { deny: SENSITIVE_DATA_DENYLIST },
                response: { deny: SENSITIVE_DATA_DENYLIST },
            },
            httpBodies: [],
            urlQueryParams: { deny: SENSITIVE_DATA_DENYLIST },
        },
    });
};
